import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/providers";
import Seo from "@/components/Seo";

const FAQS = [
  {
    q: "Who is Verdict built for?",
    a: "Verdict is built for design studios, brand teams and packaging-led founders who need a candid, on-demand second opinion on a pack before it ships. It's purpose-tuned for the Indian market — kirana, modern trade, e-commerce thumbnails, regional languages.",
  },
  {
    q: "How accurate is the AI scoring?",
    a: "Verdict is built on packaging strategy principles used by the Therefore team in real-world brand and design work. It's designed to reflect how a trained strategist would critique a pack — not just how it looks, but how well it works.",
  },
  {
    q: "How is the seven-dimension score calculated?",
    a: "Each pack image is critiqued one dimension at a time by a frontier multimodal LLM (Claude, GPT or Gemini). Every dimension returns its own score, strengths, weaknesses and improvements; a final synthesis pass produces the overall verdict and the priority recommendations.",
  },
  {
    q: "Can I use it to compare multiple packaging options?",
    a: "Yes — you can run audits on different pack variants to compare performance and make informed decisions before going to print or market.",
  },
  {
    q: "Is it useful for packaging that's already in market?",
    a: "Absolutely. Many teams use Verdict to benchmark existing packs, build a case for a redesign, or identify specific weak points to fix without a full overhaul.",
  },
  {
    q: "Do I need design expertise to interpret the results?",
    a: "No. The scores and commentary are written to be clear and actionable for brand managers and marketers, not just designers.",
  },
  {
    q: "Can I track how my packaging has improved over time?",
    a: "Yes. Verdict saves your audit history so you can revisit previous results, track progress across iterations, and see how each round of changes has moved the needle.",
  },
  {
    q: "Can I compare a refresh against the previous pack?",
    a: "Yes. Mark the new design as a sub-premise (refresh) and link it to the previous assessment, or upload the existing on-shelf pack at the brief stage. Verdict produces a side-by-side delta of all seven dimensions so you can argue for the change with the brand custodian.",
  },
  {
    q: "Can I collaborate with my team on an audit?",
    a: "Absolutely. Designers and brand leads can invite collaborators to a project. Team members can review the audit results and leave comments directly, keeping feedback in one place rather than scattered across emails and calls.",
  },
  {
    q: "How do I share results with stakeholders who aren't on the platform?",
    a: "You can email the audit results to anyone as a polished, branded PDF — no login required on their end. It's presentation-ready, so it works just as well in a client meeting as it does in an internal review.",
  },
  {
    q: "What if my design is confidential?",
    a: "Uploads are stored only on Therefore Design's own backend; nothing is sent to third-party model training pipelines. Inside the studio dashboard you can delete any project, asset or assessment at any time.",
  },
  {
    q: "How do I get access?",
    a: "Anyone with a @thereforedesign.co.in Google account signs in automatically. Outside designers (freelancers, partner agencies) request access on first sign-in; the studio lead approves you from the admin dashboard.",
  },
];

const SEVEN_DIMENSIONS = [
  "01 — Alignment to Positioning",
  "02 — E-commerce 3-second scan",
  "03 — Aisle resiliency & shelf-presence matrix",
  "04 — Traditional Indian retail presence",
  "05 — Distinctiveness vs competition",
  "06 — Hierarchy & eye-tracking flow",
  "07 — Target-audience aspiration fit",
  "08 — Portfolio Alignment",
];

// Reusable gradient-outline card for the "Section 2 · 4 easy steps" boxes.
// Uses a masking trick so the gradient shows as a border on a rounded surface
// without breaking the underlying dark background.
const gradientCardStyle = {
  position: "relative",
  padding: 28,
  borderRadius: 14,
  background: "var(--v-bg)",
  border: "1px solid transparent",
  backgroundImage:
    "linear-gradient(var(--v-bg), var(--v-bg)), linear-gradient(135deg, #06B6D4 0%, #8B5CF6 55%, #F472B6 100%)",
  backgroundOrigin: "border-box",
  backgroundClip: "padding-box, border-box",
};

// Section header helpers keep vertical rhythm consistent across the redesign.
function Strapline({ children }) {
  return <div className="overline" style={{ color: "var(--v-accent)" }}>{children}</div>;
}
function SectionHeadline({ children, id, size = "clamp(32px,4.2vw,54px)" }) {
  return (
    <h2 id={id} className="display" style={{ fontSize: size, lineHeight: 1.05, marginTop: 12, letterSpacing: "-.015em" }}>
      {children}
    </h2>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);

  const onLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const o = encodeURIComponent(window.location.origin);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login?origin=${o}`, { credentials: "include" });
      const j = await r.json();
      if (!j.auth_url) throw new Error("missing auth_url");
      window.location.href = j.auth_url;
    } catch {
      window.location.href = "/?auth_error=start_failed";
    }
  };

  const primaryCta = user ? (
    <button className="v-btn" data-testid="enter-studio-btn" onClick={() => navigate("/studio")}>
      Enter Studio →
    </button>
  ) : (
    <button className="v-btn" data-testid="google-login-btn" onClick={onLogin}>
      Sign in with Google →
    </button>
  );

  return (
    <>
      <Seo
        path="/"
        title="Verdict — AI-powered packaging design audit platform for India"
        description="Score every packaging design across seven strategic dimensions — brand alignment, e-commerce, kirana shelf, distinctiveness, hierarchy, audience fit — before it goes to print. Built by Therefore Design, Mumbai."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "@id": "https://verdict.thereforedesign.co.in/#software",
            name: "Verdict",
            alternateName: [
              "Verdict by Therefore Design",
              "Verdict Packaging Intelligence Platform",
              "HITL AI-powered Packaging Intelligence Platform",
            ],
            applicationCategory: "BusinessApplication",
            applicationSubCategory: "Packaging design audit tool",
            operatingSystem: "Web",
            description:
              "Verdict is a Human-in-the-Loop AI-powered packaging design audit platform. It critiques packaging artwork across seven strategic dimensions tuned for the Indian retail market — brand alignment, e-commerce shelf, aisle resiliency, traditional kirana retail, distinctiveness, hierarchy, and audience aspiration fit — and produces a written verdict plus a shareable PDF report.",
            url: "https://verdict.thereforedesign.co.in/",
            image: "https://verdict.thereforedesign.co.in/og-verdict.png",
            inLanguage: "en-IN",
            audience: {
              "@type": "Audience",
              audienceType:
                "Consumer-brand founders, marketing teams, design agencies, packaging designers",
            },
            featureList: [
              "AI critique across 7 packaging dimensions",
              "Comparison of design variations",
              "Refresh delta vs existing on-shelf pack",
              "System-level packaging architecture audit across categories",
              "Shareable branded PDF report",
              "Team collaboration and email delivery",
              "Deck brief extraction (PDF / PPTX)",
            ],
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "INR",
              lowPrice: "0",
              offerCount: "3",
              url: "https://verdict.thereforedesign.co.in/pricing",
              availability: "https://schema.org/InStock",
            },
            provider: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
            creator: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://verdict.thereforedesign.co.in/#website",
            url: "https://verdict.thereforedesign.co.in/",
            name: "Verdict",
            publisher: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
          },
        ]}
      />
      <div className="v-container" style={{ minHeight: "100vh", paddingTop: 96, paddingBottom: 120 }}>
        {/* ==================== SECTION 1 · Hero ==================== */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7 v-fade-in">
            <div className="overline" data-testid="landing-overline">
              HITL, AI-powered Packaging Intelligence Platform
            </div>
            <h1 className="display" style={{ fontSize: "clamp(44px, 6.4vw, 88px)", lineHeight: 0.98, marginTop: 24 }}>
              A second opinion <br /> on every pack <br /> you ship.
            </h1>
            <p style={{ marginTop: 28, maxWidth: 640, fontSize: 17, lineHeight: 1.6, color: "var(--v-text-2)" }}>
              Verdict is your packaging intelligence partner for the Indian market. Powered by AI
              and decades of packaging expertise, it critiques every design across seven strategic
              dimensions—from shelf impact and brand alignment to e-commerce performance and
              consumer relevance. More importantly, it builds an evolving knowledge base by
              remembering every project, every variant and every recommendation, enabling every
              new launch to benefit from the intelligence of every decision that came before.
            </p>
            <div style={{ marginTop: 36, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              {primaryCta}
              <button
                type="button"
                className="v-btn v-btn-outline"
                data-testid="how-it-works-link"
                onClick={() => setVideoOpen(true)}
              >
                How it works ▶
              </button>
            </div>
            {!user && (
              <div
                data-testid="landing-trial-pill"
                onClick={onLogin}
                style={{
                  marginTop: 22,
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "8px 16px 8px 8px",
                  borderRadius: 999, border: "1px solid var(--v-accent)",
                  background: "rgba(6,182,212,0.10)", cursor: "pointer",
                  fontSize: 13, color: "var(--v-text)",
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "var(--v-accent)", color: "#0A0A0A",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 13,
                }} aria-hidden>1</span>
                <span><b style={{ color: "var(--v-accent)" }}>First audit free</b> — no card needed. Sign in to claim.</span>
              </div>
            )}
          </div>

          <div className="md:col-span-5 v-fade-in-d2">
            <div className="v-surface" style={{ padding: 24 }}>
              <div className="overline">Seven dimensions</div>
              <ul style={{ marginTop: 16, listStyle: "none", padding: 0, lineHeight: 1.8 }}>
                {SEVEN_DIMENSIONS.slice(0, 7).map((s) => (
                  <li key={s} style={{ borderBottom: "1px solid var(--v-border)", padding: "10px 0", fontSize: 14 }}>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ==================== SECTION 2 · 4 easy steps ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-steps">
          <SectionHeadline size="clamp(28px,4vw,44px)">4 easy steps, less than 5 minutes.</SectionHeadline>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{ marginTop: 40 }}>
            {[
              { n: "01", t: "Brief the brand", b: "Positioning, target audience, competitors, aspirations. Once per project — Verdict remembers it forever." },
              { n: "02", t: "Upload the pack", b: "Drag the design. Add competitor refs. Pick your AI critic — Claude, GPT, or Gemini." },
              { n: "03", t: "Read the verdict", b: "Seven scores, candid feedback, priority fixes. Compare against last round, side-by-side." },
              { n: "04", t: "Mail it to all stakeholders", b: "Whether it's team lead, your client or your colleague, simply invite them to collaborate." },
            ].map((s) => (
              <div key={s.n} style={gradientCardStyle} data-testid={`step-card-${s.n}`}>
                <div className="mono" style={{ color: "var(--v-accent)", fontSize: 12, letterSpacing: ".14em" }}>{s.n}</div>
                <h3 style={{ fontSize: 20, marginTop: 12, letterSpacing: "-.01em" }}>{s.t}</h3>
                <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.55 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== SECTION 3 · Built on ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-built-on">
          <Strapline>Built on</Strapline>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" style={{ marginTop: 24 }}>
            {[
              { big: "15+", small: "Years of packaging strategy" },
              { big: "1,000+", small: "Packaging systems analysed" },
              { big: "200+", small: "Brands' insights inside" },
              { big: "Award", small: "Award-winning branding consultancy" },
              { big: "HITL", small: "Human-in-the-loop AI intelligence" },
            ].map((s) => (
              <div key={s.small} className="v-surface" style={{ padding: 24 }} data-testid={`built-on-${s.big.replace(/[^a-z0-9]/gi, "")}`}>
                <div className="v-score" style={{ fontSize: 42, lineHeight: 1, color: "var(--v-accent)" }}>{s.big}</div>
                <div className="mono" style={{ marginTop: 12, color: "var(--v-text-2)", fontSize: 12, lineHeight: 1.5 }}>{s.small}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== SECTION 4 · The problem ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-problem">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <Strapline>The problem</Strapline>
              <SectionHeadline>Packaging decisions worth crores are still made on opinions or instinct.</SectionHeadline>
            </div>
            <div className="md:col-span-7">
              <p style={{ fontSize: 17, lineHeight: 1.7, color: "var(--v-text-2)", marginTop: 12 }}>
                Every launch involves endless debates.
              </p>
              <ul style={{ marginTop: 16, paddingLeft: 0, listStyle: "none", display: "grid", gap: 10 }}>
                {[
                  `"Make the logo bigger."`,
                  `"Let's use more red."`,
                  `"Consumers will understand."`,
                  `"I like Option B."`,
                ].map((q) => (
                  <li key={q} className="mono" style={{
                    padding: "10px 14px",
                    borderLeft: "2px solid var(--v-accent)",
                    color: "var(--v-text)", fontStyle: "italic",
                    fontSize: 14,
                  }}>{q}</li>
                ))}
              </ul>
              <p style={{ marginTop: 24, fontSize: 17, lineHeight: 1.7, color: "var(--v-text)" }}>
                Unfortunately, packaging doesn&apos;t succeed because everyone in the boardroom agrees.
              </p>
              <p style={{ marginTop: 10, fontSize: 17, lineHeight: 1.7, color: "var(--v-text-2)" }}>
                It succeeds because consumers notice it, understand it and choose it.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* ==================== SEPARATOR 1 · Black band with gradient text ==================== */}
      <section
        data-testid="separator-1"
        style={{
          background: "#000",
          padding: "clamp(56px, 8vw, 120px) 24px",
          overflow: "hidden",
        }}
      >
        <div className="v-container" style={{ padding: 0 }}>
          <p
            className="display"
            style={{
              fontFamily: "'Exo', 'Exo 2', sans-serif",
              fontWeight: 200,
              fontSize: "clamp(32px, 6vw, 96px)",
              lineHeight: 1.08,
              letterSpacing: "-.015em",
              margin: 0,
              textAlign: "left",
              backgroundImage: "linear-gradient(135deg, #06B6D4 0%, #8B5CF6 55%, #F472B6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}
          >
            Verdict replaces subjective opinions <br /> with structured intelligence.
          </p>
        </div>
      </section>

      <div className="v-container" style={{ paddingBottom: 120 }}>
        {/* ==================== SECTION 5 · Framework ==================== */}
        <section id="framework" style={{ marginTop: 140 }} data-testid="section-framework">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <Strapline>What the platform assesses</Strapline>
              <SectionHeadline>Your packaging deserves more than a design review.</SectionHeadline>
              <p style={{ marginTop: 20, color: "var(--v-text-2)", fontSize: 15, lineHeight: 1.6, maxWidth: 460 }}>
                Instead of simply judging aesthetics, Verdict evaluates your packaging across seven
                strategic dimensions that influence real-world performance.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["01", "Alignment to Positioning", "Does your packaging communicate the right brand and value proposition?"],
                  ["02", "E-commerce 3-second scan", "Will digital shoppers notice it within seconds?"],
                  ["03", "Aisle resiliency & shelf-presence matrix", "Does your packaging work hard on the modern retail shelf?"],
                  ["04", "Traditional Indian retail presence", "Will it stand apart on a crowded kirana shelf?"],
                  ["05", "Distinctiveness vs competition", "Does your packaging stand apart from the competition and, at the same time, belong?"],
                  ["06", "Hierarchy & eye-tracking flow", "Does the pack answer key purchase questions in the right order?"],
                  ["07", "Target-audience aspiration fit", "Will consumers immediately relate to it and instantly recognise your brand?"],
                  ["08", "Portfolio alignment", "Does it strengthen your overall brand architecture?"],
                ].map(([n, t, b]) => (
                  <div key={n} className="v-surface" style={{ padding: 20 }} data-testid={`framework-${n}`}>
                    <div className="mono" style={{ color: "var(--v-accent)", fontSize: 11, letterSpacing: ".12em" }}>{n}</div>
                    <h4 style={{ marginTop: 8, fontSize: 15, letterSpacing: "-.005em" }}>{t}</h4>
                    <p style={{ marginTop: 6, color: "var(--v-text-2)", fontSize: 13, lineHeight: 1.55 }}>{b}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 28 }}>
                <button className="v-btn" data-testid="framework-cta" onClick={() => user ? navigate("/studio") : onLogin()}>
                  Explore the Framework →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 6 · Modules ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-modules">
          <Strapline>Packaging intelligence modules</Strapline>
          <SectionHeadline>One platform. Multiple packaging decisions.</SectionHeadline>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{ marginTop: 40 }}>
            {[
              ["New packaging assessment", "Launching a new brand? Evaluate market readiness before production."],
              ["Packaging refresh stress-test", "Planning a redesign? Understand whether you're strengthening your brand — or quietly losing recognition."],
              ["Portfolio expansion", "Launching a new SKU? Ensure every new product fits seamlessly into your brand architecture."],
              ["Variation testing", "Confused which design to take ahead? Compare different packaging options to pick your winning design."],
            ].map(([t, b], idx) => (
              <div key={t} className="v-surface" style={{ padding: 24, display: "flex", flexDirection: "column" }} data-testid={`module-${idx}`}>
                <div className="mono" style={{ color: "var(--v-accent)", fontSize: 11, letterSpacing: ".14em" }}>{String(idx + 1).padStart(2, "0")}</div>
                <h4 style={{ marginTop: 10, fontSize: 18, letterSpacing: "-.01em" }}>{t}</h4>
                <p style={{ marginTop: 10, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.6 }}>{b}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 32 }}>
            <button className="v-btn" data-testid="modules-cta" onClick={() => user ? navigate("/studio") : onLogin()}>
              Claim your first free audit →
            </button>
          </div>
        </section>

        {/* ==================== SECTION 7 · Who it's for ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-audience">
          <Strapline>Who should use this platform</Strapline>
          <SectionHeadline>Built for everyone shaping consumer brands.</SectionHeadline>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{ marginTop: 40 }}>
            {[
              ["Founders", "Validate major packaging investments."],
              ["Marketing teams", "Launch with confidence."],
              ["Design agencies", "Support recommendations with objective evidence."],
              ["Packaging designers", "Stress-test concepts before presentation."],
            ].map(([t, b], idx) => (
              <div key={t} className="v-surface" style={{ padding: 24 }} data-testid={`audience-${idx}`}>
                <h4 style={{ fontSize: 20, letterSpacing: "-.01em" }}>{t}</h4>
                <p style={{ marginTop: 12, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.6 }}>{b}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 32 }}>
            <button className="v-btn" data-testid="audience-cta" onClick={() => user ? navigate("/studio") : onLogin()}>
              Claim your first free audit →
            </button>
          </div>
        </section>

        {/* ==================== SECTION 8 · What every report contains ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-report">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6">
              <Strapline>What you get</Strapline>
              <SectionHeadline>Every assessment generates a comprehensive Packaging Intelligence Report.</SectionHeadline>
            </div>
            <div className="md:col-span-6">
              <ul style={{ padding: 0, margin: 0, listStyle: "none", display: "grid", gap: 14 }}>
                {[
                  "Overall Verdict with high-priority recommendations",
                  "Packaging Health Score per dimension and aggregated",
                  "Detailed breakdown of strengths and weaknesses across 7 dimensions",
                  "Clear recommendations for improving on each dimension",
                ].map((line) => (
                  <li key={line} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 15, lineHeight: 1.55 }}>
                    <span style={{ color: "var(--v-accent)", fontWeight: 700, marginTop: 1 }}>✓</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 28 }}>
                <a
                  className="v-btn"
                  href="/email_previews/sample_report.pdf"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="report-cta"
                >
                  Download Sample Report ↓
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 9 · Final CTA ==================== */}
        <section style={{ marginTop: 140 }} data-testid="section-final-cta">
          <div className="v-surface" style={{ padding: "clamp(32px, 5vw, 64px)", textAlign: "center" }}>
            <SectionHeadline size="clamp(30px, 4.6vw, 56px)">
              Before you print thousands of packs, get one verdict that matters.
            </SectionHeadline>
            <div style={{ display: "grid", gap: 8, marginTop: 24, color: "var(--v-text-2)", fontSize: 16, lineHeight: 1.6 }}>
              <div>Reduce uncertainty.</div>
              <div>Validate your decisions.</div>
              <div>Launch with confidence.</div>
              <div>Avoid expensive packaging mistakes.</div>
            </div>
            <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="v-btn" data-testid="final-cta-primary" onClick={() => user ? navigate("/studio") : onLogin()}>
                Run your first Verdict →
              </button>
              <a
                className="v-btn v-btn-outline"
                href="/email_previews/sample_report.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="final-cta-secondary"
              >
                See Sample Report ↓
              </a>
            </div>
            <p className="mono" style={{ marginTop: 28, color: "var(--v-text-2)", fontSize: 12, lineHeight: 1.6, letterSpacing: ".04em" }}>
              Because the most expensive packaging mistake is the one you discover after launch.
            </p>
          </div>
        </section>

        {/* ==================== FAQ (unchanged) ==================== */}
        <section id="faq" data-testid="faq-section" style={{ marginTop: 140 }}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="overline">FAQ</div>
              <h2 className="display" style={{ fontSize: "clamp(34px, 4.5vw, 56px)", lineHeight: 1.05, marginTop: 12, letterSpacing: "-.015em" }}>
                The honest questions.
              </h2>
              <p style={{ marginTop: 16, color: "var(--v-text-2)", maxWidth: 380, fontSize: 14, lineHeight: 1.6 }}>
                The ones designers ask before they hit upload — and the ones brand custodians ask after.
              </p>
            </div>
            <div className="md:col-span-8" data-testid="faq-accordion">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div
                    key={f.q}
                    data-testid={`faq-item-${i}`}
                    style={{ borderTop: "1px solid var(--v-border)", borderBottom: i === FAQS.length - 1 ? "1px solid var(--v-border)" : "none" }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      aria-expanded={open}
                      data-testid={`faq-trigger-${i}`}
                      style={{
                        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                        gap: 16, padding: "22px 0",
                        background: "transparent", border: "none", textAlign: "left",
                        color: "inherit", cursor: "pointer",
                      }}
                    >
                      <span style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-.01em" }}>{f.q}</span>
                      <span
                        aria-hidden="true"
                        style={{
                          fontSize: 22, fontWeight: 300, color: "var(--v-accent)",
                          transform: open ? "rotate(45deg)" : "rotate(0deg)",
                          transition: "transform .25s ease",
                          lineHeight: 1,
                        }}
                      >+</span>
                    </button>
                    <div
                      data-testid={`faq-content-${i}`}
                      style={{
                        maxHeight: open ? 420 : 0,
                        overflow: "hidden",
                        transition: "max-height .35s ease",
                      }}
                    >
                      <p style={{ paddingBottom: 22, color: "var(--v-text-2)", fontSize: 15, lineHeight: 1.65, maxWidth: 640 }}>
                        {f.a}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* ==================== "How it works" video modal ==================== */}
      {videoOpen && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="how-it-works-modal"
          onClick={() => setVideoOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "rgba(0,0,0,.82)",
            backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "min(1000px, 100%)",
              aspectRatio: "16/9",
              background: "var(--v-bg)",
              border: "1px solid var(--v-border)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              data-testid="how-it-works-modal-close"
              aria-label="Close video"
              style={{
                position: "absolute", top: 12, right: 12, zIndex: 2,
                width: 34, height: 34, borderRadius: 999,
                background: "rgba(0,0,0,.55)", border: "1px solid var(--v-border)",
                color: "#fff", cursor: "pointer", fontSize: 16,
              }}
            >
              ×
            </button>
            {/* Placeholder — the actual walkthrough video will be dropped in
                here. When the user supplies the video URL, swap this block for
                a <video src=… controls autoPlay /> or an <iframe /> for a
                YouTube/Vimeo embed. */}
            <div style={{
              width: "100%", height: "100%",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 16, padding: 40, textAlign: "center",
            }}>
              <div className="overline" style={{ color: "var(--v-accent)" }}>How it works</div>
              <h3 className="display" style={{ fontSize: "clamp(24px,3vw,36px)", lineHeight: 1.1 }}>
                Walkthrough video coming soon.
              </h3>
              <p style={{ color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.6, maxWidth: 440 }}>
                A 60-second product walkthrough will play here. Drop your video file or embed URL
                into <code className="mono">/app/frontend/src/pages/Landing.jsx</code> and it will replace this placeholder.
              </p>
              <Link
                to={user ? "/studio" : "#"}
                onClick={(e) => { if (!user) { e.preventDefault(); onLogin(); } }}
                className="v-btn"
                data-testid="how-it-works-modal-cta"
                style={{ marginTop: 4 }}
              >
                {user ? "Enter Studio →" : "Sign in to try it →"}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
